import moment from "moment";

export const PROMISE_LINE =
  "Your Astra advisors typically respond within 1 business day.";
export const HEADER = "Your Astra advisors";
export const PLACEHOLDER = "Write to your advisors…";
export const WELCOME_SENDER = "Astra STARs";
export const AUTOMATIC_PILL = "Automatic message";

// Calendar-day distance, not a raw 24h-multiple, and identical to mobile's
// threadTranscript.ts dayLabel: comparing calendar days (via startOf('day'))
// keeps the Today/Yesterday/weekday-name/absolute-date cutovers stable
// regardless of the two timestamps' time-of-day, whereas a straight
// moment#diff on the original instants can floor to a different day count
// depending on clock time and shift the boundary by one day. The `> 0` lower
// bound matters too: without it a future-dated createdAt renders as a weekday
// name instead of its date.
export function dayLabel(iso, now = new Date()) {
  const d = moment(iso);
  const n = moment(now);
  const diffDays = n
    .clone()
    .startOf("day")
    .diff(d.clone().startOf("day"), "days");
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 0 && diffDays < 7) return d.format("dddd");
  return d.format("MMM D, YYYY");
}

// The two viewers the thread components render for. SP1's client screen
// passes nothing and gets CLIENT_VIEWER; the SP2 advisor inbox passes
// { kind: 'advisor', _id: <the signed-in advisor> }.
export const CLIENT_VIEWER = { kind: "client", _id: null };

export const isOwnAdvisorMessage = (message, viewer) =>
  viewer?.kind === "advisor" &&
  message.senderType === "advisor" &&
  !!viewer._id &&
  String(message.sender?._id) === String(viewer._id);

// Who a message is from, from where the viewer sits: the row's colour
// (thread transcript spec § Who is which colour). "you" is the signed-in
// person — the client on /thread, the advisor on their own replies.
export function messageRole(message, viewer = CLIENT_VIEWER) {
  if (message.kind === "welcome" || message.senderType === "system")
    return "system";
  if (viewer?.kind === "advisor") {
    if (message.senderType === "client") return "client";
    return isOwnAdvisorMessage(message, viewer) ? "you" : "advisor";
  }
  return message.senderType === "client" ? "you" : "advisor";
}

// The attribution line as its bold name and its muted rest (decided item 1);
// `name + rest` is attributionLine exactly. The client's own label is their
// first name, "You" only when there is none (decided item 4).
export function attributionParts(message, viewer = CLIENT_VIEWER) {
  if (message.kind === "welcome" || message.senderType === "system")
    return { name: WELCOME_SENDER, rest: "" };
  if (message.senderType === "client") {
    const fallback = viewer?.kind === "advisor" ? "Client" : "You";
    return { name: message.sender?.firstName || fallback, rest: "" };
  }
  const first = message.sender?.firstName || "Advisor";
  const you = isOwnAdvisorMessage(message, viewer) ? " (you)" : "";
  const title = message.sender?.title || "Astra advisor";
  const handoff = message.handoffFrom?.firstName
    ? ` · picking up from ${message.handoffFrom.firstName}`
    : "";
  return { name: `${first}${you}`, rest: ` · ${title}${handoff}` };
}

export function attributionLine(message, viewer = CLIENT_VIEWER) {
  const { name, rest } = attributionParts(message, viewer);
  return `${name}${rest}`;
}

export const initials = (a) =>
  `${a.firstName?.[0] ?? ""}${a.lastName?.[0] ?? ""}`.toUpperCase();

// SP2-W13: splits a message's raw text into text/link parts so a URL typed
// into the body can be rendered as a clickable link without ever touching
// dangerouslySetInnerHTML. Matches http(s):// and bare www. runs of
// non-whitespace, case-insensitively; trailing punctuation and closing
// quotes are left outside the link as plain text (so "see https://a.com."
// links only the URL). A bare www. match only counts as a link at the start
// of the text or after an allowed preceding character (see
// WWW_PRECEDING_OK) (so "xwww.evil.com" stays plain text).
const URL_RUN = /(https?:\/\/\S+|www\.\S+)/gi;
// SP2-W15 review round (Opus): "]" gets the same paren-style balancing as
// ")" (see trimTrailingPunctuation below); ">" is simply never valid
// unencoded in a URL, so it is always trimmed with no balancing against a
// preceding "<".
const TRAILING_CHARS = ".,;:!?)'\"’”>]";
// SP2-W15 (a): a bare www. match only counts as a link at the start of the
// text or right after one of these — whitespace, an opening bracket/quote
// (ASCII or the curly “), so "www." inside a quoted or bracketed address
// still links while "xwww.evil.com" (no recognised preceding character)
// stays plain text.
const WWW_PRECEDING_OK = /[\s("'[<“]/;
// After trimming, what's left must still look like a URL — a prefix with at
// least one more character after it — or trimming ate into the prefix
// itself ("www..." -> "www", "https://." -> "https://") and there is nothing
// left to link.
const LOOKS_LIKE_A_LINK = /^(?:https?:\/\/|www\.)./i;

// Trims TRAILING_CHARS off the end of `value` by walking in from the end by
// index. A regex anchored at the end ([...]+$) is quadratic here: when the
// string does NOT end in one of those characters, the engine backtracks
// through the whole run at every starting position before failing. This
// walk only ever looks at the characters it actually trims, plus one final
// slice, so it stays linear regardless of how long the trailing run is.
//
// SP2-W15 (b): a trailing ")" is kept, rather than trimmed, when the body
// still has more "(" than ")" before it — so a URL like
// ".../wiki/Foo_(bar)" keeps its closing paren instead of losing it to the
// generic trailing-punctuation trim, while a ")" with nothing to balance it
// ("(see https://a.com)") still comes off. "]" gets the identical treatment
// against "[" (review round: "]" was being kept unconditionally, the same
// bug the ")" balancing already fixed for parens). The open/close counts
// for each bracket kind are taken once over the whole token (a single regex
// pass each, no backtracking hazard), then updated in O(1) per character
// trimmed, so the walk stays linear overall.
function trimTrailingPunctuation(value) {
  const totalOpenParen = (value.match(/\(/g) || []).length;
  const totalCloseParen = (value.match(/\)/g) || []).length;
  const totalOpenBracket = (value.match(/\[/g) || []).length;
  const totalCloseBracket = (value.match(/\]/g) || []).length;
  let trimmedCloseParen = 0;
  let trimmedCloseBracket = 0;
  let end = value.length;
  while (end > 0 && TRAILING_CHARS.includes(value[end - 1])) {
    const c = value[end - 1];
    if (c === ")") {
      // The ")" at end-1 is excluded from the body; bodyClose also excludes
      // it (the "- 1") and every ")" already trimmed off after it.
      const bodyClose = totalCloseParen - trimmedCloseParen - 1;
      if (totalOpenParen > bodyClose) break;
      trimmedCloseParen += 1;
    } else if (c === "]") {
      const bodyClose = totalCloseBracket - trimmedCloseBracket - 1;
      if (totalOpenBracket > bodyClose) break;
      trimmedCloseBracket += 1;
    }
    end -= 1;
  }
  return value.slice(0, end);
}

export function linkifyParts(text) {
  if (!text) return [];
  const parts = [];
  let cursor = 0;
  let match;
  URL_RUN.lastIndex = 0;
  while ((match = URL_RUN.exec(text))) {
    const raw = match[0];
    const isBareWww = /^www\./i.test(raw);
    const precededOk =
      !isBareWww ||
      match.index === 0 ||
      WWW_PRECEDING_OK.test(text[match.index - 1]);
    const value = trimTrailingPunctuation(raw);
    if (!precededOk || !LOOKS_LIKE_A_LINK.test(value)) continue;
    if (match.index > cursor) {
      parts.push({ type: "text", value: text.slice(cursor, match.index) });
    }
    const href = /^www\./i.test(value) ? `https://${value}` : value;
    parts.push({ type: "link", value, href });
    cursor = match.index + value.length;
  }
  if (cursor < text.length)
    parts.push({ type: "text", value: text.slice(cursor) });
  return parts;
}

// The avatar's letters for a row: the sender's, or "AS" for the welcome and
// system lines (from "Astra STARs", decided item 2).
export function messageInitials(message) {
  if (message.kind === "welcome" || message.senderType === "system") {
    const [firstName, lastName] = WELCOME_SENDER.split(" ");
    return initials({ firstName, lastName });
  }
  return initials(message.sender || {});
}
